import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AdminLayout } from "@/components/AdminLayout";
import { ScrollToTop } from "@/components/ScrollToTop";

// Public Pages
import HomePage from "@/pages/HomePage";
import QuotePage from "@/pages/QuotePage";
import PricingPage from "@/pages/PricingPage";
import FAQPage from "@/pages/FAQPage";
import InsurancePage from "@/pages/InsurancePage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import DisclaimerPage from "@/pages/DisclaimerPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import QuoteResultPage from "@/pages/QuoteResultPage";
import ResumePaymentPage from "@/pages/ResumePaymentPage";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import DashboardPage from "@/pages/admin/DashboardPage";
import VisitorConsentDialog from "@/components/VisitorConsentDialog";
import LoadsPage from "@/pages/admin/LoadsPage";
import LoadDetailPage from "@/pages/admin/LoadDetailPage";
import CarriersPage from "@/pages/admin/CarriersPage";
import CarrierProfilePage from "@/pages/admin/CarrierProfilePage";
import CarrierVettingPage from "@/pages/admin/CarrierVettingPage";
import CarrierSearchPage from "@/pages/admin/CarrierSearchPage";
import ShippersPage from "@/pages/admin/ShippersPage";
import AuditLogsPage from "@/pages/admin/AuditLogsPage";
import ConnectorHealthPage from "@/pages/admin/ConnectorHealthPage";
import KPIPage from "@/pages/admin/KPIPage";
import NotificationsPage from "@/pages/admin/NotificationsPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import EventsPage from "@/pages/admin/EventsPage";
import SubscriptionsPage from "@/pages/admin/SubscriptionsPage";
import PaymentsReportPage from "@/pages/admin/reports/PaymentsReportPage";
import LoadPerformancePage from "@/pages/admin/reports/LoadPerformancePage";
import CarrierScorecardPage from "@/pages/admin/reports/CarrierScorecardPage";
import FraudDashboardPage from "@/pages/admin/FraudDashboardPage";
import FraudCasesPage from "@/pages/admin/FraudCasesPage";
import FraudCaseDetailPage from "@/pages/admin/FraudCaseDetailPage";
import IPBlocklistPage from "@/pages/admin/IPBlocklistPage";
import ShipTeslaPage from "@/pages/ShipTeslaPage";
import TeslaShippingCost from "@/pages/TeslaShippingCost";
import EnclosedTeslaTransportPage from "@/pages/EnclosedTeslaTransportPage";
import CaliforniaToTexasRoutePage from "@/pages/CaliforniaToTexasRoutePage";
import TeslaToHawaiiRoutePage from "@/pages/TeslaToHawaiiRoutePage";
import CaliforniaToCarolinaRoutePage from "@/pages/CaliforniaToCarolinaRoutePage";
import BlogPage from "@/pages/BlogPage";
import BlogPost1 from "@/pages/BlogPost1";
import BlogPost2 from "@/pages/BlogPost2";
import BlogPost3 from "@/pages/BlogPost3";
import BlogPost4 from "@/pages/BlogPost4";
import BlogPost5 from "@/pages/BlogPost5";
import BlogPost6 from "@/pages/BlogPost6";
import BlogPost7 from "@/pages/BlogPost7";
import CDPriceCheckPage from "@/pages/admin/CDPriceCheckPage";
import CDListingsPage from "@/pages/admin/CDListingsPage";
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <VisitorConsentDialog />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/quote/:loadId" element={<QuoteResultPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/insurance" element={<InsurancePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/resume/:token" element={<ResumePaymentPage />} />
            <Route path="/ship-a-tesla" element={<ShipTeslaPage />} />

            <Route
              path="/tesla-shipping-cost"
              element={<TeslaShippingCost />}
            />
            <Route
              path="/enclosed-tesla-transport"
              element={<EnclosedTeslaTransportPage />}
            />
            <Route
              path="/routes/california-to-texas"
              element={<CaliforniaToTexasRoutePage />}
            />
            <Route
              path="/routes/tesla-to-hawaii"
              element={<TeslaToHawaiiRoutePage />}
            />

            <Route
              path="/routes/california-to-carolina"
              element={<CaliforniaToCarolinaRoutePage />}
            />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/how-to-ship-a-tesla" element={<BlogPost1 />} />
            <Route
              path="/blog/tesla-shipping-cost-2026"
              element={<BlogPost2 />}
            />
            <Route
              path="/blog/why-tesla-specialized-shipping"
              element={<BlogPost3 />}
            />
            <Route
              path="/blog/can-tesla-be-towed-flatbed-only"
              element={<BlogPost4 />}
            />
            <Route path="/blog/broker-vs-carrier" element={<BlogPost5 />} />
            <Route
              path="/blog/tesla-shipping-vs-driving"
              element={<BlogPost6 />}
            />
            <Route
              path="/blog/tesla-transport-vs-gas-vehicles"
              element={<BlogPost7 />}
            />
            {/* Admin Routes — AdminLayout persists across navigation */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<DashboardPage />} />
              <Route path="/admin/loads" element={<LoadsPage />} />
              <Route path="/admin/loads/:loadId" element={<LoadDetailPage />} />
              <Route path="/admin/carriers" element={<CarriersPage />} />
              <Route
                path="/admin/carriers/search"
                element={<CarrierSearchPage />}
              />
              <Route
                path="/admin/carriers/:carrierId"
                element={<CarrierProfilePage />}
              />
              <Route
                path="/admin/carriers/:carrierId/vetting"
                element={<CarrierVettingPage />}
              />
              <Route path="/admin/shippers" element={<ShippersPage />} />
              <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
              <Route
                path="/admin/connector-health"
                element={<ConnectorHealthPage />}
              />
              <Route path="/admin/kpi" element={<KPIPage />} />
              <Route
                path="/admin/notifications"
                element={<NotificationsPage />}
              />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/events" element={<EventsPage />} />
              <Route
                path="/admin/subscriptions"
                element={<SubscriptionsPage />}
              />
              <Route
                path="/admin/reports/payments"
                element={<PaymentsReportPage />}
              />
              <Route
                path="/admin/reports/load-performance"
                element={<LoadPerformancePage />}
              />
              <Route
                path="/admin/reports/carrier-scorecard"
                element={<CarrierScorecardPage />}
              />
              <Route path="/admin/fraud" element={<FraudDashboardPage />} />
              <Route path="/admin/fraud/cases" element={<FraudCasesPage />} />
              <Route
                path="/admin/fraud/cases/:caseId"
                element={<FraudCaseDetailPage />}
              />
              <Route
                path="/admin/fraud/blocklist"
                element={<IPBlocklistPage />}
              />
              <Route
                path="/admin/cd-price-check"
                element={<CDPriceCheckPage />}
              />
              <Route path="/admin/cd-listings" element={<CDListingsPage />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
